import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Box, Text, AccordionPanel,
} from 'grommet';
import { adminLogOutRequest } from '@Actions';
import { paths } from '@Components/configs';
import { AppButton, ThemeToggleButton } from '@Components/Control';
import { useAppTheme } from '@Components/Context';
import { localAsset } from '@Helpers/asset';
import {
  NavbarSmallLayout,
  NavbarAuthLayout,
  MenuDrop,
  ProfileSection,
  MobileMenuButton,
  MenuAnchor,
} from './HeaderComponent';
import {
  StyledProfileDropDownAnchor,
  StyledAccordion,
} from './StyledHeader';
import { StyledSVG } from '../Sidenav/StyledAuthNavWrapper';
import style from './Header.module.scss';

const AdminHeader = ({
  small,
  isAdminAuthenticated,
  location,
  logOut,
  currentUserEmail = null,
}) => {
  const [showOverlay, setShowOverlay] = React.useState(false);
  const [isScroll, setIsScroll] = React.useState(false);
  const [collapseOverride, setCollapseOverride] = React.useState(false);
  const authNavbarRef = React.useRef(null);
  const {
    theme: { page, navigation, button },
    accountPlaceholder,
    isDarkMode,
    setDarkMode,
  } = useAppTheme();

  React.useEffect(() => {
    window.addEventListener('scroll', () => {
      if (showOverlay || collapseOverride || small) return;
      const navbar = authNavbarRef.current;
      if (!navbar) return;
      const scrollThreshold = Math.ceil(navbar.getBoundingClientRect().top + 25);
      setIsScroll(window.scrollY >= scrollThreshold);
    });
    setCollapseOverride(false);
    return () => {
      window.removeEventListener('scroll', () => {});
    };
  }, [location.pathname]);

  if (!isAdminAuthenticated) {
    return null;
  }

  if (small) {
    return (
      <NavbarSmallLayout
        darkBackground={isDarkMode}
        navbarRef={authNavbarRef}
        toggle={() => setShowOverlay(!showOverlay)}
        showOverlay={showOverlay}
        navBg={navigation.background}
        navBorder={navigation.border}
        primaryTextColor={page.primaryText}
        secondaryMenuContent={(
          <MobileMenuButton
            label="Sign Out"
            path={paths.login}
            onClick={() => {
              logOut();
              setShowOverlay(!showOverlay);
            }}
            buttonColor={button.primaryBg}
            icon={(
              <Box flex={false} width="20px" height="20px" margin={{ right: '12px' }}>
                <StyledSVG
                  src={localAsset('images/sign-in-icon.svg')}
                  height="20px"
                  width="20px"
                  $fillColor={button.iconColor}
                />
              </Box>
            )}
          />
        )}
      >
        <Box flex direction="column" gap="1.5rem" pad={{ top: '1.5rem' }}>
          <StyledAccordion
            iconColor="var(--button-icon-color)"
            highlightColor="var(--button-icon-hover-color)"
          >
            <AccordionPanel label={<MenuAnchor label="File Imports" color={page.primaryText} />}>
              <Box align="stretch" pad={{ horizontal: '1rem' }}>
                <MenuAnchor
                  path={paths.adminFileImportNew.replace('/:id?', '')}
                  label="New file import"
                  onClick={() => setShowOverlay(!showOverlay)}
                  color={page.primaryText}
                  className={style.accordionItem}
                />
                <MenuAnchor
                  path={paths.adminCreateSearchesImport}
                  label="Create searches"
                  onClick={() => setShowOverlay(!showOverlay)}
                  color={page.primaryText}
                  className={style.accordionItem}
                />
                <MenuAnchor
                  path={paths.adminFileImportsHistory}
                  label="File imports history"
                  onClick={() => setShowOverlay(!showOverlay)}
                  color={page.primaryText}
                  className={style.accordionItem}
                />
              </Box>
            </AccordionPanel>
            <AccordionPanel label={<MenuAnchor label="Searches" color={page.primaryText} />}>
              <Box align="stretch" pad={{ horizontal: '1rem' }}>
                <MenuAnchor
                  path={paths.adminSearches}
                  label="All searches"
                  onClick={() => setShowOverlay(!showOverlay)}
                  color={page.primaryText}
                  className={style.accordionItem}
                />
              </Box>
            </AccordionPanel>
            <AccordionPanel label={<MenuAnchor label="Results" color={page.primaryText} />}>
              <Box align="stretch" pad={{ horizontal: '1rem' }}>
                <MenuAnchor
                  path={paths.adminResults}
                  label="All results"
                  onClick={() => setShowOverlay(!showOverlay)}
                  color={page.primaryText}
                  className={style.accordionItem}
                />
              </Box>
            </AccordionPanel>
            <AccordionPanel label={<MenuAnchor label="Compliance" color={page.primaryText} />}>
              <Box align="stretch" pad={{ horizontal: '1rem' }}>
                <MenuAnchor
                  path={paths.adminCompliance}
                  label="DMCA notices"
                  onClick={() => setShowOverlay(!showOverlay)}
                  color={page.primaryText}
                  className={style.accordionItem}
                />
              </Box>
            </AccordionPanel>
            <AccordionPanel label={<MenuAnchor label="Content ID" color={page.primaryText} />}>
              <Box align="stretch" pad={{ horizontal: '1rem' }}>
                <MenuAnchor
                  path={paths.adminMonetizationRequests}
                  label="Monetization requests"
                  onClick={() => setShowOverlay(!showOverlay)}
                  color={page.primaryText}
                  className={style.accordionItem}
                />
              </Box>
            </AccordionPanel>
            <AccordionPanel label={<MenuAnchor label="Content ID" color={page.primaryText} />}>
              <Box align="stretch" pad={{ horizontal: '1rem' }}>
                <MenuAnchor
                  path={paths.adminManualClaims}
                  label="Manual claim requests"
                  onClick={() => setShowOverlay(!showOverlay)}
                  color={page.primaryText}
                  className={style.accordionItem}
                />
              </Box>
            </AccordionPanel>
          </StyledAccordion>
          <Box
            flex={false}
            direction="column"
            gap="1rem"
            pad={{ top: '2rem', left: '2.2rem' }}
            border={{
              side: 'top', color: navigation.border, style: 'solid', size: '1px',
            }}
          >
            <Box>
              <Text
                uppercase
                size="15px"
                weight={600}
                color={page.secondaryText}
                style={{ letterSpacing: '2.8px' }}
              >
                Admin User
              </Text>
            </Box>
            <Box>
              <MenuAnchor
                path={paths.adminAccount}
                label="Account"
                onClick={() => setShowOverlay(!showOverlay)}
                color={page.primaryText}
              />
              <MenuAnchor
                label="Update theme"
                onClick={() => {
                  setDarkMode(!isDarkMode);
                  setShowOverlay(!showOverlay);
                }}
                color={page.primaryText}
              />
            </Box>
          </Box>
          <Box flex direction="column" justify="end" pad={{ bottom: '1.5rem', horizontal: '1.85rem' }} gap="1.5rem">
            <ProfileSection
              small
              title="Admin"
              subtitle={currentUserEmail}
              logoUrl={null}
              placeholder={accountPlaceholder}
              outlineColor={page.highlight}
              textColor={page.primaryText}
            />
          </Box>
        </Box>
      </NavbarSmallLayout>
    );
  }

  return (
    <NavbarAuthLayout
      navbarRef={authNavbarRef}
      isScroll={collapseOverride ? true : isScroll}
      navBg={navigation.background}
      darkMode={isDarkMode}
      borderColor={navigation.border}
    >
      <Box direction="row" flex={{ grow: 2 }} justify="center" align="center" gap="0.5rem">
        <Box
          width={{ min: 'fit-content' }}
          pad="0.5rem"
          background={button.hoverBg}
          round="var(--app-radius)"
        >
          <Text size="1rem" weight={600} color={button.hoverText} style={{ letterSpacing: '0.5px' }}>
            ADMIN
          </Text>
        </Box>
        <Box direction="row" width="100%" justify="center" align="center" gap="0.5rem">
          <Box width="fit-content">
            <MenuDrop
              withIcon
              navBg={navigation.background}
              customPad="1.5rem 0.25rem 1.5rem 1.25rem"
              hoverHighlight={page.altHighlight}
              anchorComp={(
                <AppButton
                  fill
                  dropDown
                  overrideHover
                  textSize="1rem"
                  fontWeight="600"
                  label="File Imports"
                />
              )}
            >
              {(closeHover) => (
                <Box pad="12px">
                  <StyledProfileDropDownAnchor
                    label="New File Import"
                    onClick={closeHover}
                    href={paths.adminFileImportNew.replace('/:id?', '')}
                    primaryText={page.primaryText}
                    hoverColor={button.hoverBg}
                    highlightText={button.hoverText}
                  />
                  <StyledProfileDropDownAnchor
                    label="Create Searches"
                    onClick={closeHover}
                    href={paths.adminCreateSearchesImport}
                    primaryText={page.primaryText}
                    hoverColor={button.hoverBg}
                    highlightText={button.hoverText}
                  />
                  <StyledProfileDropDownAnchor
                    label="File Imports History"
                    onClick={closeHover}
                    href={paths.adminFileImportsHistory}
                    primaryText={page.primaryText}
                    hoverColor={button.hoverBg}
                    highlightText={button.hoverText}
                  />
                </Box>
              )}
            </MenuDrop>
          </Box>
        </Box>
      </Box>
      <Box direction="row" align="center" margin={{ right: '1rem' }} flex={{ grow: 0 }}>
        <ThemeToggleButton
          iconSize="1.4rem"
          setUserPreference={() => {
            // For admin users, preference is only persisted in localStorage
          }}
        />
      </Box>
      <MenuDrop
        navBg={navigation.background}
        profileProps={{
          darkBackground: false,
          small: false,
          outlineColor: page.highlight,
          title: 'Admin User',
          subTitle: currentUserEmail,
          logoUrl: null,
          placeholder: accountPlaceholder,
          textColor: page.primaryText,
          iconColor: button.iconColor,
        }}
      >
        {(closeHover) => (
          <Box pad="12px">
            <Box pad={{ vertical: '0.5rem', horizontal: '1rem' }} direction="column" gap="1rem">
              <Text size="0.85rem" color={page.altHighlight}>
                {currentUserEmail}
              </Text>
            </Box>
            <StyledProfileDropDownAnchor
              label="Account"
              onClick={closeHover}
              href={paths.adminAccount}
              primaryText={page.primaryText}
              hoverColor={button.hoverBg}
              highlightText={button.hoverText}
            />
            <StyledProfileDropDownAnchor
              hoverColor={button.hoverBg}
              primaryText={page.primaryText}
              highlightText={button.hoverText}
              label={(
                <Box direction="row" align="center" gap="0.5rem">
                  <Box flex={false} width="fit-content" height="fit-content" className="signOutIcon">
                    <StyledSVG
                      src={localAsset('images/sign-in-icon.svg')}
                      height="22px"
                      width="22px"
                      $fillColor={button.iconColor}
                    />
                  </Box>
                  Sign Out
                </Box>
              )}
              onClick={logOut}
            />
          </Box>
        )}
      </MenuDrop>
    </NavbarAuthLayout>
  );
};

function mapStateToProps(state) {
  const {
    adminEmail, isAdminAuthenticated,
  } = state.adminAuth;

  return {
    currentUserEmail: adminEmail,
    isAdminAuthenticated,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    logOut: adminLogOutRequest,
  }, dispatch);
}

AdminHeader.propTypes = {
  small: PropTypes.bool.isRequired,
  isAdminAuthenticated: PropTypes.bool.isRequired,
  currentUserEmail: PropTypes.string,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  logOut: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminHeader);
